import { ChangeServerLink } from '@tinycld/core/components/ChangeServerLink'
import { ReviewModeHints } from '@tinycld/core/components/connect/ReviewModeHints'
import { OrgLogo } from '@tinycld/core/components/OrgLogo'
import { requestPasswordReset } from '@tinycld/core/lib/account-password'
import { useAuth } from '@tinycld/core/lib/auth'
import { takePendingRoute } from '@tinycld/core/lib/pending-route'
import {
    useSignInNotice,
    useSignInNoticeStore,
} from '@tinycld/core/lib/stores/sign-in-notice-store'
import { useThemeColor } from '@tinycld/core/lib/use-app-theme'
import type { OrgBranding as OrgBrandingInfo } from '@tinycld/core/lib/use-org-info'
import { useOrgInfo } from '@tinycld/core/lib/use-org-info'
import { useDeviceInsets } from '@tinycld/core/lib/use-safe-area'
import { router } from 'expo-router'
import { useState } from 'react'
import {
    ActivityIndicator,
    KeyboardAvoidingView,
    Platform,
    Pressable,
    Text,
    TextInput,
    View,
} from 'react-native'

type Mode = 'login' | 'reset'

export function LoginModal() {
    const backdropColor = useThemeColor('overlay-backdrop')
    const [mode, setMode] = useState<Mode>('login')
    const insets = useDeviceInsets()

    return (
        <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            className="absolute top-0 left-0 right-0 bottom-0 justify-center items-center"
            style={{
                zIndex: 200,
                backgroundColor: backdropColor,
                // The backdrop deliberately still covers the full screen — only
                // the card is inset. In landscape the sensor housing sits on one
                // side (~59pt), so a centred card can otherwise have its edge
                // clipped by the notch on a narrow window.
                paddingLeft: insets.left,
                paddingRight: insets.right,
                paddingTop: insets.top,
                paddingBottom: insets.bottom,
            }}
        >
            <View
                className="rounded-2xl border border-border p-8 bg-background"
                style={{
                    width: 400,
                    maxWidth: '90%',
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 8 },
                    shadowOpacity: 0.15,
                    shadowRadius: 24,
                    elevation: 8,
                }}
            >
                <LoginForm isVisible={mode === 'login'} onForgotPassword={() => setMode('reset')} />
                <ResetForm isVisible={mode === 'reset'} onBack={() => setMode('login')} />
            </View>
        </KeyboardAvoidingView>
    )
}

function LoginForm({
    isVisible,
    onForgotPassword,
}: {
    isVisible: boolean
    onForgotPassword: () => void
}) {
    const mutedColor = useThemeColor('muted-foreground')
    const primaryFg = useThemeColor('primary-foreground')
    const { login } = useAuth({ throwIfAnon: false })
    const notice = useSignInNotice()
    const clearNotice = useSignInNoticeStore(s => s.clear)
    // Pre-auth: reads the unauthenticated /api/org-info endpoint, so it
    // resolves fine before the user signs in. `org` is null while loading or
    // on an unbranded deployment — OrgBranding degrades to nothing rather
    // than an empty circle in that case.
    const { org } = useOrgInfo()
    const [identifier, setIdentifier] = useState('')
    const [password, setPassword] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const canSubmit = identifier.trim().length > 0 && password.length > 0 && !isSubmitting

    const handleSubmit = async () => {
        if (!canSubmit) return
        setError(null)
        setIsSubmitting(true)
        const result = await login(identifier.trim(), password)
        if (result.error) {
            setError(result.error)
            setIsSubmitting(false)
        } else if (result.user) {
            // The notice (if any) explained why this sign-in was needed; a
            // fresh session no longer needs the explanation, and it must not
            // reappear if this user is later routed back to sign-in for an
            // unrelated reason.
            clearNotice()
            // Return to whatever route the gate interrupted — a deep link, or
            // the screen an expired session flipped out from under the user.
            // Recorded by AuthGate; replace (not push) so the sign-in overlay
            // isn't a back-button destination.
            const pending = takePendingRoute()
            if (pending) {
                router.replace(pending)
            } else {
                // Nothing to return to: send every signed-in user to the
                // app root.
                router.replace('/')
            }
        }
    }

    if (!isVisible) return null

    return (
        <>
            <OrgBranding org={org} />

            <Text className="text-[22px] font-bold mb-1 text-foreground">Sign in</Text>
            <Text className="text-sm mb-6 text-muted-foreground">
                Sign in to your account to continue
            </Text>

            <SignInNotice isVisible={!!notice} notice={notice} />

            <SignInError isVisible={!!error} error={error} />

            <View className="mb-4">
                <Text className="mb-1.5 text-sm font-semibold text-foreground">
                    Username or email
                </Text>
                <TextInput
                    className="border border-border rounded-lg p-3 text-base text-foreground bg-surface-secondary"
                    testID="identifier"
                    value={identifier}
                    onChangeText={setIdentifier}
                    placeholder="alice or alice@company.com"
                    placeholderTextColor={mutedColor}
                    autoCapitalize="none"
                    autoCorrect={false}
                    autoComplete="username"
                    editable={!isSubmitting}
                />
            </View>

            <View className="mb-2">
                <Text className="mb-1.5 text-sm font-semibold text-foreground">Password</Text>
                <TextInput
                    className="border border-border rounded-lg p-3 text-base text-foreground bg-surface-secondary"
                    testID="login-password"
                    value={password}
                    onChangeText={setPassword}
                    placeholder="Password"
                    placeholderTextColor={mutedColor}
                    secureTextEntry
                    autoComplete="current-password"
                    editable={!isSubmitting}
                    onSubmitEditing={handleSubmit}
                />
            </View>

            <Pressable
                testID="login-submit"
                accessibilityRole="button"
                className={`rounded-lg items-center mt-4 p-3.5 bg-primary ${canSubmit ? 'opacity-100' : 'opacity-50'}`}
                onPress={handleSubmit}
                disabled={!canSubmit}
            >
                {isSubmitting ? (
                    <ActivityIndicator color={primaryFg} size="small" />
                ) : (
                    <Text className="text-base font-semibold text-primary-foreground">Sign in</Text>
                )}
            </Pressable>

            <ReviewModeHints
                onPrefill={(id, password) => {
                    setIdentifier(id)
                    setPassword(password)
                }}
            />

            <View className="mt-4 flex-row justify-around">
                <Pressable
                    testID="forgot-password-link"
                    accessibilityRole="link"
                    onPress={onForgotPassword}
                >
                    <Text className="text-xs text-muted-foreground underline">
                        Forgot password?
                    </Text>
                </Pressable>
                <ChangeServerLink />
            </View>
        </>
    )
}

function SignInNotice({ isVisible, notice }: { isVisible: boolean; notice: string | null }) {
    if (!isVisible) return null

    return (
        <View className="rounded-lg p-3 mb-4 bg-info-soft" testID="sign-in-notice">
            <Text className="text-sm text-info">{notice}</Text>
        </View>
    )
}

function SignInError({ isVisible, error }: { isVisible: boolean; error: string | null }) {
    if (!isVisible) return null

    return (
        <View className="rounded-lg p-3 mb-4 bg-danger-soft" testID="sign-in-error">
            <Text className="text-sm text-danger">{error}</Text>
        </View>
    )
}

// Exercises the same pre-login rendering path org_branding's public read
// rule exists for. Renders nothing when org is null (loading, or a
// deployment with no server-reported name) rather than an empty circle.
function OrgBranding({ org }: { org: OrgBrandingInfo | null }) {
    if (!org) return null

    return (
        <View className="items-center mb-4">
            <OrgLogo org={org} size={52} />
            <Text className="text-sm font-semibold mt-2 text-foreground">{org.name}</Text>
        </View>
    )
}

function ResetForm({ isVisible, onBack }: { isVisible: boolean; onBack: () => void }) {
    const mutedColor = useThemeColor('muted-foreground')
    const primaryFg = useThemeColor('primary-foreground')
    const [email, setEmail] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSent, setIsSent] = useState(false)

    const canSubmit = email.trim().length > 0 && !isSubmitting

    const handleSubmit = async () => {
        if (!canSubmit) return
        setIsSubmitting(true)
        // requestPasswordReset never throws and never reveals whether the email
        // exists — we always land on the same confirmation.
        await requestPasswordReset(email.trim())
        setIsSubmitting(false)
        setIsSent(true)
    }

    if (!isVisible) return null

    return (
        <>
            <Text className="text-[22px] font-bold mb-1 text-foreground">Reset password</Text>
            <Text className="text-sm mb-6 text-muted-foreground">
                Enter your email and we'll send you a reset link
            </Text>

            {isSent ? (
                <View className="rounded-lg p-3 mb-4 bg-surface-secondary" testID="reset-sent">
                    <Text className="text-sm text-foreground">
                        If an account exists for that email, we've sent a reset link. Check your
                        inbox.
                    </Text>
                </View>
            ) : (
                <View className="mb-2">
                    <Text className="mb-1.5 text-sm font-semibold text-foreground">Email</Text>
                    <TextInput
                        className="border border-border rounded-lg p-3 text-base text-foreground bg-surface-secondary"
                        testID="reset-email"
                        value={email}
                        onChangeText={setEmail}
                        placeholder="alice@company.com"
                        placeholderTextColor={mutedColor}
                        autoCapitalize="none"
                        autoCorrect={false}
                        keyboardType="email-address"
                        autoComplete="email"
                        editable={!isSubmitting}
                        onSubmitEditing={handleSubmit}
                    />
                </View>
            )}

            {!isSent && (
                <Pressable
                    testID="reset-submit"
                    className={`rounded-lg items-center mt-2 p-3.5 bg-primary ${canSubmit ? 'opacity-100' : 'opacity-50'}`}
                    onPress={handleSubmit}
                    disabled={!canSubmit}
                >
                    {isSubmitting ? (
                        <ActivityIndicator color={primaryFg} size="small" />
                    ) : (
                        <Text className="text-base font-semibold text-primary-foreground">
                            Send reset link
                        </Text>
                    )}
                </Pressable>
            )}

            <Pressable
                testID="reset-back"
                accessibilityRole="link"
                onPress={onBack}
                className="mt-4 items-center"
            >
                <Text className="text-xs text-muted-foreground underline">Back to sign in</Text>
            </Pressable>
        </>
    )
}
